import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import useTriggerFromAWorkflowStore, { getTriggerFromAWorkflowStore } from './useTriggerFromAWorkflowStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';

// Utils
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import {
  send_component_save_request,
  send_request,
} from '../../utils/Request';
import { loadDraftProject } from '../../utils/canvasApi';
import { deriveMissingTypesInSavedPlaceholders } from '../../utils/LoopModeUtil';
import uuid from 'uuid';

// MUI
import Grid from '@mui/material/Grid';
import { Button, CircularProgress } from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import { styled } from '@mui/material/styles';

// Custom
import { ErrorText, StyledErrorIcon } from './components/StyledComponents';
import RenderConnectionData from './components/RenderConnectionData';
import Dropdowns from './components/Dropdowns';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { LoopModeToggle } from './components/LoopModeToggle';
import CustomPaper from '../common/CustomPaper';

const LoadingContainer = styled(Grid)({
  marginTop: '16px',
});

const LoadingText = styled('span')({
  marginLeft: '12px',
  fontSize: '14px',
});

const RefreshButtonContainer = styled(Grid)({
  marginTop: '16px',
  marginBottom: '16px',
});

const RefreshButton = styled(Button)({
  textTransform: 'none',
});

const TriggerFromAWorkflowTemplate = (props) => {
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [components, setComponents] = useState([]);
  const [sourceProjectId, setSourceProjectId] = useState('');
  const [sourceComponentId, setSourceComponentId] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [connectionData, setConnectionData] = useState(null);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [focus, setFocus] = useState('');
  const [projectsRefreshing, setProjectsRefreshing] = useState(false);
  const [componentsRefreshing, setComponentsRefreshing] = useState(false);
  const [loopMode, setLoopMode] = useState(false);

  const prevSourceProjectIdRef = useRef('');

  const store = useMemo(() => getTriggerFromAWorkflowStore(props.component_id), [props.component_id]);

  const componentName = useProjectStore((s) => s.componentName);
  const componentDescription = useProjectStore((s) => s.componentDescription);
  const useBpmnJoinBehavior = useProjectStore((s) => s.useBpmnJoinBehavior);

  const renderProjects = useCallback((data) => {
    return data.map((project) => ({
      label: project['projectName'] ? project['projectName'] : project['projectId'],
      id: project['projectId'],
    }));
  }, []);

  const renderComponents = useCallback((data) => {
    const comps = data.components;
    if (comps == null) return [];
    return comps
      .filter((comp) => comp['type'] === 'trigger_another_workflow')
      .map((comp) => {
        if (comp['componentData']) {
          return {
            label: comp['componentData']['name']
              ? comp['componentData']['name']
              : comp['componentId'],
            id: comp['componentId'],
          };
        } else {
          return {
            label: comp['componentId'],
            id: comp['componentId'],
          };
        }
      });
  }, []);

  const retrieveComponentsByProjectId = useCallback(
    async (projectId) => {
      try {
        const res = await loadDraftProject(projectId);
        setComponents(renderComponents(res.data));
      } catch (e) {
        console.log(e);
      }
    },
    [renderComponents]
  );

  const refreshProjects = useCallback(async () => {
    try {
      setProjectsRefreshing(true);
      const response = await send_request(`project-service/project/getProjectsNames`, '', {});
      setProjects(renderProjects(response.data));
      setProjectsRefreshing(false);
      return true;
    } catch (err) {
      console.error('Error refreshing projects:', err);
      setProjectsRefreshing(false);
      return false;
    }
  }, [renderProjects]);

  const refreshComponents = useCallback(async () => {
    const currentSourceProjectId = sourceProjectId;
    if (currentSourceProjectId) {
      try {
        setComponentsRefreshing(true);
        const response = await loadDraftProject(currentSourceProjectId);
        setComponents(renderComponents(response.data));
        setComponentsRefreshing(false);
        return true;
      } catch (e) {
        console.log(e);
        setComponentsRefreshing(false);
        return false;
      }
    }
    return false;
  }, [sourceProjectId, renderComponents]);

  const refreshAll = useCallback(async () => {
    await refreshProjects();
    if (sourceProjectId) {
      await refreshComponents();
    }
  }, [refreshProjects, refreshComponents, sourceProjectId]);

  const connectTriggers = useCallback(() => {
    setButtonLoading(true);
    const url = `project-service/project/component/${sourceComponentId}`;
    send_request(url, '', '', 'get')
      .then((res) => {
        const componentData = res.data.componentData;
        const componentDataData = componentData.data;
        if (componentData && componentDataData && componentDataData.savedPlaceholders) {
          setButtonLoading(false);
          setError(false);
          setErrorMessage('');
          setConnectionData({
            placeholders: componentDataData.savedPlaceholders.map((val) => ({
              id: uuid(),
              sourceKey: val.key,
              sourceValue: val.value,
              targetKey: val.key.replace(/[^\w]/gi, '_'),
              targetValue: val.value,
              type: val.type,
              length: val.length,
            })),
            sourceComponentId,
            sourceProjectId,
            connectionTime: Date.now(),
          });
        } else {
          setButtonLoading(false);
          setError(true);
          setErrorMessage('There was an error in your source component configuration');
        }
      })
      .catch((err) => {
        console.log(err);
        setButtonLoading(false);
        setConnectionData(null);
      });
  }, [sourceComponentId, sourceProjectId]);

  const deriveMissingTypes = useCallback(
    (phs) => deriveMissingTypesInSavedPlaceholders(phs, connectionData),
    [connectionData]
  );

  const handleSaveComponent = useCallback(() => {
    let cData = store.getState().getTemplate().data;
    cData.name = componentName;
    cData.sourceProjectId = sourceProjectId;
    cData.sourceComponentId = sourceComponentId;
    cData.connectionData = connectionData;
    cData.loopMode = loopMode;

    let lastModified = getCurrentTime();
    let data = injectMetadataFields({
      componentData: {
        data: cData,
        name: componentName,
        lastModified: lastModified,
      },
      componentId: props.component_id,
      type: 'trigger_from_a_workflow',
      description: componentDescription,
    });

    send_component_save_request(
      'component-service/triggerFromAWorkflow/data',
      data,
      '',
      'POST'
    )
      .then((response) => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(data);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch((err) => {
        throw err;
      });
  }, [componentName, componentDescription, sourceProjectId, sourceComponentId, connectionData, loopMode, props]);

  // Placeholder subscription
  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key, isNotPlaceholder } = placeholder['selected'];
        if (!style) return;
        insertPlaceholder(style.default, key, style, focus, isNotPlaceholder);
      }
    );
    return unsub;
  }, [focus]);

  const insertPlaceholder = useCallback(
    (label, text, style, currentFocus, isNotPlaceholder) => {
      if (currentFocus === 'editor' || currentFocus === 'subject') {
        setPlaceholders((prev) => [...prev, { label, text, style, focus: currentFocus }]);
      } else {
        toInput(label, text, style, currentFocus, isNotPlaceholder);
      }
    },
    []
  );

  // Track sourceProjectId changes to fetch components
  useEffect(() => {
    if (sourceProjectId && sourceProjectId !== prevSourceProjectIdRef.current) {
      retrieveComponentsByProjectId(sourceProjectId);
    }
    prevSourceProjectIdRef.current = sourceProjectId;
  }, [sourceProjectId, retrieveComponentsByProjectId]);

  // componentDidMount logic
  useEffect(() => {
    if (props.component_id == undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);

      if (!projects || projects.length === 0) {
        send_request('project-service/project/getProjectsNames', '', {}).then((response) => {
          if (response?.data) {
            setProjects(renderProjects(response.data));
          }
        });
      }

      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then((response) => {
        if (
          response.data &&
          response.data.components &&
          response.data.components[0].componentData
        ) {
          const {
            projectId,
            onComponentNameChanged,
            onComponentDescriptionChanged,
            onLastModifiedChanged,
          } = props;

          const storeState = store.getState();
          storeState.setTriggerFromAWorkflowTemplate(response.data.components[0]);
          store.setState({ loaded: true });
          onComponentNameChanged(store.getState().name);
          onComponentDescriptionChanged(response.data.components[0].description);
          onLastModifiedChanged(store.getState().lastModified);

          const cData = response.data.components[0].componentData;
          const cDataData = cData.data;
          const isProjectIdMissing = !cDataData.sourceProjectId && cDataData.sourceComponentId;

          const loadedSourceComponentId = cDataData.sourceComponentId;
          const loadedSourceProjectId = isProjectIdMissing ? projectId : cDataData.sourceProjectId;
          setSourceProjectId(loadedSourceProjectId);
          setSourceComponentId(loadedSourceComponentId);
          setLoopMode(cDataData.loopMode || false);

          if (cDataData.sourceProjectId) {
            retrieveComponentsByProjectId(cDataData.sourceProjectId);
          }

        }
        props.setChildTemplateLoaded(true);
      })
      .then(() => {
        refreshProjects()
          .then(() => {
            setLoading(false);
          })
          .catch((err) => {
            setLoading(false);
            throw Error(err.message);
          });
      })
      .catch((err) => {
        throw Error(err.message);
      });
  }, []);

  // Auto-connect after loading completes
  const loadingRef = useRef(true);
  useEffect(() => {
    if (loadingRef.current && !loading) {
      loadingRef.current = false;
      if (
        sourceProjectId &&
        sourceComponentId
      ) {
        connectTriggers();
      }
    }
  }, [loading, sourceProjectId, sourceComponentId, connectTriggers]);

  const handleLoopModeToggle = useCallback(
    (event) => {
      const isToggled = event.target.checked;
      setLoopMode(isToggled);
      if (isToggled) {
        const currentProjectId = props.project_id;
        setSourceProjectId(currentProjectId);
        retrieveComponentsByProjectId(currentProjectId);
      }
    },
    [props.project_id, retrieveComponentsByProjectId]
  );

  const updateState = useCallback((newState) => {
    if (newState.placeholders !== undefined) setPlaceholders(newState.placeholders);
    if (newState.loading !== undefined) setLoading(newState.loading);
    if (newState.projects !== undefined) setProjects(newState.projects);
    if (newState.components !== undefined) setComponents(newState.components);
    if (newState.sourceProjectId !== undefined) setSourceProjectId(newState.sourceProjectId);
    if (newState.sourceComponentId !== undefined) setSourceComponentId(newState.sourceComponentId);
    if (newState.buttonLoading !== undefined) setButtonLoading(newState.buttonLoading);
    if (newState.connectionData !== undefined) setConnectionData(newState.connectionData);
    if (newState.error !== undefined) setError(newState.error);
    if (newState.errorMessage !== undefined) setErrorMessage(newState.errorMessage);
    if (newState.focus !== undefined) setFocus(newState.focus);
    if (newState.projectsRefreshing !== undefined) setProjectsRefreshing(newState.projectsRefreshing);
    if (newState.componentsRefreshing !== undefined) setComponentsRefreshing(newState.componentsRefreshing);
    if (newState.loopMode !== undefined) setLoopMode(newState.loopMode);
  }, []);

  if (loading) return <IndividualComponentLoader />;

  return (
    <CustomPaper>
      {!useBpmnJoinBehavior && (
        <LoopModeToggle loopMode={loopMode} onChange={handleLoopModeToggle} />
      )}
      <Dropdowns
        placeholders={placeholders}
        loading={loading}
        projects={projects}
        components={components}
        sourceProjectId={sourceProjectId}
        sourceComponentId={sourceComponentId}
        buttonLoading={buttonLoading}
        connectionData={connectionData}
        error={error}
        errorMessage={errorMessage}
        focus={focus}
        projectsRefreshing={projectsRefreshing}
        componentsRefreshing={componentsRefreshing}
        loopMode={loopMode}
        getComponents={retrieveComponentsByProjectId}
        updateState={async (newState) => updateState(newState)}
        currProjectId={props.project_id}
        handleSaveComponent={handleSaveComponent}
        refreshAll={refreshAll}
        refreshProjects={refreshProjects}
        refreshComponents={refreshComponents}
        connectTriggers={connectTriggers}
      />

      {buttonLoading && (
        <LoadingContainer container item xs={12}>
          <CircularProgress size={24} />
          <LoadingText>Establishing connection...</LoadingText>
        </LoadingContainer>
      )}

      {sourceProjectId && sourceComponentId && (
        <>
          {error && (
            <Grid item xs={12}>
              <ErrorText>
                <StyledErrorIcon />
                {errorMessage}
              </ErrorText>
            </Grid>
          )}
          {connectionData && !buttonLoading && (
            <>
              <RefreshButtonContainer item xs={12}>
                <RefreshButton
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={connectTriggers}
                  disabled={buttonLoading}
                >
                  Refresh Connection
                </RefreshButton>
              </RefreshButtonContainer>
              <RenderConnectionData
                placeholders={placeholders}
                loading={loading}
                projects={projects}
                components={components}
                sourceProjectId={sourceProjectId}
                sourceComponentId={sourceComponentId}
                buttonLoading={buttonLoading}
                connectionData={connectionData}
                error={error}
                errorMessage={errorMessage}
                focus={focus}
                projectsRefreshing={projectsRefreshing}
                componentsRefreshing={componentsRefreshing}
                loopMode={loopMode}
                updateState={(newState) => updateState(newState)}
              />
            </>
          )}
        </>
      )}
    </CustomPaper>
  );
};

export default TriggerFromAWorkflowTemplate;
